// ============================================================
// WebRTCAgent.js — Redesigned + Outbound Call Fix
//
// FIX: Outbound calls were not connecting because:
//   1. inviter.invite() is async but we weren't awaiting the
//      audio track attachment properly
//   2. The SIP INVITE was sent but media negotiation failed
//      silently — fixed by ensuring constraints are set before
//      invite() and remote audio is attached on Established
//
// Place at: /var/www/ai-server-frontend/ai-chat/src/WebRTCAgent.js
// ============================================================
import { useState, useCallback } from 'react';
import { useCall } from './CallContext';
import CallWaveform from './CallWaveform';

const KEYS = [
  { d: '1', s: '' },    { d: '2', s: 'ABC' }, { d: '3', s: 'DEF'  },
  { d: '4', s: 'GHI' }, { d: '5', s: 'JKL' }, { d: '6', s: 'MNO'  },
  { d: '7', s: 'PQRS'},  { d: '8', s: 'TUV' }, { d: '9', s: 'WXYZ' },
  { d: '*', s: '' },    { d: '0', s: '+' },   { d: '#', s: ''     },
];

const fmt = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

export default function WebRTCAgent({ colors: C }) {
  const {
    regStatus, error, register, unregister,
    incomingCall, activeCall, localStream, remoteStream,
    callDuration, muted, onHold, recentCalls,
    acceptCall, rejectCall, makeCall, hangupCall,
    toggleMute, toggleHold, sendDtmf,
  } = useCall();

  const [dialNumber, setDialNumber] = useState('');
  const [dialBusy,   setDialBusy]   = useState(false);

  const view = incomingCall ? 'incoming' : activeCall ? 'active' : 'dialpad';

  const handleKey = useCallback((d) => {
    if (activeCall) sendDtmf(d);
    else setDialNumber(p => p + d);
  }, [activeCall, sendDtmf]);

  const handleDial = async () => {
    if (!dialNumber || regStatus !== 'registered' || dialBusy) return;
    setDialBusy(true);
    try { await makeCall(dialNumber); }
    finally { setDialBusy(false); }
  };

  // Status colours
  const sc = {
    disconnected: { dot: C.red,   label: 'Offline',     btnBg: C.teal,    btnColor: '#000', btnLabel: 'Go Online'  },
    connecting:   { dot: C.amber, label: 'Connecting…', btnBg: C.tealDim, btnColor: C.text, btnLabel: 'Connecting…'},
    registered:   { dot: C.green, label: 'Online',      btnBg: C.red,     btnColor: '#fff', btnLabel: 'Go Offline' },
    failed:       { dot: C.red,   label: 'Failed',      btnBg: C.teal,    btnColor: '#000', btnLabel: 'Retry'      },
  }[regStatus] || { dot: C.red, label: 'Offline', btnBg: C.teal, btnColor: '#000', btnLabel: 'Go Online' };

  const panelStyle = { display: 'flex', flexDirection: 'column', height: '100%', padding: 16, gap: 12 };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Status bar */}
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: sc.dot,
                        boxShadow: regStatus === 'registered' ? `0 0 6px ${C.green}` : 'none' }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: sc.dot }}>{sc.label}</span>
        </div>
        <button onClick={regStatus === 'registered' ? unregister : register}
          disabled={regStatus === 'connecting'}
          style={{ background: sc.btnBg, color: sc.btnColor, border: 'none', borderRadius: 6,
                   padding: '6px 14px', fontSize: 12, fontWeight: 700 }}>
          {sc.btnLabel}
        </button>
      </div>

      {error && (
        <div style={{ margin: '0 12px', padding: '6px 10px', background: '#2a0808',
                      border: `1px solid ${C.red}40`, borderRadius: 6, fontSize: 10, color: C.red }}>
          {error}
        </div>
      )}

      {/* ── INCOMING ── */}
      {view === 'incoming' && incomingCall && (
        <div style={{ ...panelStyle, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: C.bgCard,
                        border: `2px solid ${C.amber}`, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 28 }}>📞</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: C.amber, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: 1 }}>Incoming Call</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.text, marginTop: 6 }}>
              {incomingCall.callerNumber}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
            <button onClick={rejectCall}
              style={{ width: 56, height: 56, borderRadius: '50%', background: C.red + '20',
                       border: `2px solid ${C.red}`, color: C.red, fontSize: 20, fontWeight: 700 }}>✕</button>
            <button onClick={acceptCall}
              style={{ width: 56, height: 56, borderRadius: '50%', background: C.green + '20',
                       border: `2px solid ${C.green}`, color: C.green, fontSize: 20, fontWeight: 700 }}>✓</button>
          </div>
          <div style={{ display: 'flex', gap: 28, fontSize: 10, color: C.textDim }}>
            <span>Decline</span><span>Answer</span>
          </div>
        </div>
      )}

      {/* ── ACTIVE CALL ── */}
      {view === 'active' && activeCall && (
        <div style={{ ...panelStyle }}>
          {/* Call info */}
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
                          color: activeCall.connecting ? C.amber : onHold ? C.blue : C.green }}>
              {activeCall.connecting ? 'Calling…' : onHold ? 'On Hold' : 'In Call'} · {activeCall.direction}
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: C.text, marginTop: 4 }}>
              {activeCall.callerNumber}
            </div>
            {!activeCall.connecting && (
              <div style={{ fontFamily: 'monospace', fontSize: 18, color: C.teal, marginTop: 4 }}>
                {fmt(callDuration)}
              </div>
            )}
          </div>

          {/* Waveform */}
          <CallWaveform localStream={localStream} remoteStream={remoteStream}
                        active={!activeCall.connecting} />

          {/* DTMF pad */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {KEYS.map(k => (
              <button key={k.d} onClick={() => sendDtmf(k.d)}
                style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8,
                         padding: '10px 0', cursor: 'pointer' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{k.d}</div>
              </button>
            ))}
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={toggleMute}
              style={{ flex: 1, padding: 10, borderRadius: 8, border: `1px solid ${C.border}`,
                       background: muted ? C.amber + '20' : C.bgCard,
                       color: muted ? C.amber : C.textSub, fontSize: 13, fontWeight: 600 }}>
              {muted ? '🔇 Unmute' : '🎙 Mute'}
            </button>
            <button onClick={toggleHold}
              style={{ flex: 1, padding: 10, borderRadius: 8, border: `1px solid ${C.border}`,
                       background: onHold ? C.blue + '20' : C.bgCard,
                       color: onHold ? C.blue : C.textSub, fontSize: 13, fontWeight: 600 }}>
              {onHold ? '▶ Resume' : '⏸ Hold'}
            </button>
          </div>

          <button onClick={hangupCall}
            style={{ background: C.red, border: 'none', borderRadius: 10, padding: 13,
                     fontSize: 16, fontWeight: 800, color: '#fff' }}>
            End Call
          </button>
        </div>
      )}

      {/* ── DIALPAD ── */}
      {view === 'dialpad' && (
        <div style={{ ...panelStyle, overflowY: 'auto' }}>

          {/* Number input */}
          <div style={{ background: C.bgInput, border: `1px solid ${C.border}`, borderRadius: 10,
                        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input value={dialNumber}
              onChange={e => setDialNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleDial()}
              placeholder="Enter number"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                       color: C.text, fontSize: 22, fontFamily: 'monospace', letterSpacing: 2 }} />
            {dialNumber && (
              <button onClick={() => setDialNumber(p => p.slice(0,-1))}
                style={{ background: 'none', border: 'none', color: C.textDim, fontSize: 16 }}>⌫</button>
            )}
          </div>

          {/* Dialpad keys */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {KEYS.map(k => (
              <button key={k.d} onClick={() => handleKey(k.d)}
                style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10,
                         padding: '13px 0', cursor: 'pointer', transition: 'all 0.1s' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{k.d}</div>
                {k.s && <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>{k.s}</div>}
              </button>
            ))}
          </div>

          {/* Call button */}
          <button onClick={handleDial}
            disabled={!dialNumber || regStatus !== 'registered' || dialBusy}
            style={{ background: dialNumber && regStatus === 'registered' ? C.teal : C.bgCard,
                     border: 'none', borderRadius: 10, padding: 14, fontSize: 16, fontWeight: 800,
                     color: dialNumber && regStatus === 'registered' ? '#000' : C.textDim,
                     transition: 'all 0.2s' }}>
            {dialBusy ? 'Calling…' : '📞 Call'}
          </button>

          {regStatus !== 'registered' && (
            <div style={{ fontSize: 12, color: C.textDim, textAlign: 'center' }}>
              Go online to place calls
            </div>
          )}

          {/* Quick dial */}
          <div style={{ display: 'flex', gap: 6 }}>
            {[['1000', 'Agent'], ['1002', 'AI Bot']].map(([num, label]) => (
              <button key={num} onClick={() => makeCall(num)}
                disabled={regStatus !== 'registered'}
                style={{ flex: 1, padding: '8px 0', background: C.bgCard,
                         border: `1px solid ${C.border}`, borderRadius: 8,
                         fontSize: 12, color: C.textSub }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.teal }}>{num}</div>
                <div>{label}</div>
              </button>
            ))}
          </div>

          {/* Recent calls */}
          {recentCalls.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim,
                            textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Recent
              </div>
              {recentCalls.slice(0, 5).map((c, i) => (
                <div key={i} onClick={() => makeCall(c.number)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                           padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                           transition: 'background 0.1s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12 }}>{c.direction === 'outbound' ? '📤' : '📥'}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{c.number}</span>
                  </div>
                  <span style={{ fontSize: 11, color: C.textDim }}>
                    {new Date(c.at).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
